/**
 * File Conversion Service - FastAPI Integration
 * Converts PPTX, DOC, and TXT files to PDF using FastAPI backend
 */
import { IPublicClientApplication } from '@azure/msal-browser';
import { arrayBufferToBase64 } from '../base64.utils';

export interface ConversionResult {
  success: boolean;
  pdfBase64?: string;
  pdfFileName?: string;
  error?: string;
}

/**
 * Convert PPTX, DOC, or TXT file to PDF using FastAPI backend
 * @param file - The file to convert (PPTX, DOC, or TXT)
 * @param fastApiEndpoint - FastAPI conversion endpoint URL (should be /convert-to-pdf)
 * @param msalInstance - MSAL instance for authentication
 * @returns Converted file metadata or original if conversion fails
 */
export async function convertFileToPdf(
  file: File,
  fastApiEndpoint: string,
  msalInstance: IPublicClientApplication
): Promise<{ name: string; type: string; data: string }> {

  try {
    // Get access token
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      throw new Error('No authenticated user found');
    }

    const account = accounts[0];
    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: [`${import.meta.env.VITE_CLIENT_ID || '262b88ff-8a8d-49fc-8485-017906bd3c29'}/user_impersonation`],
      account: account,
    });

    const accessToken = tokenResponse.accessToken;
    // Convert file to base64
    const fileBase64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          const base64 = result.split(',')[1];
          resolve(base64);
        } else if (result instanceof ArrayBuffer) {
          resolve(arrayBufferToBase64(result));
        } else {
          reject(new Error('Unexpected result type'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    // Prepare request payload matching FastAPI format
    const requestPayload = {
      request_id: `conv_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`,
      timestamp: new Date().toISOString(),
      metadata: {
        source: 'react'
      },
      files: [
        {
          filename: file.name,
          content_base64: fileBase64
        }
      ]
    };

    // Call FastAPI endpoint with JSON payload and Authorization header
    const response = await fetch(fastApiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/json'
      },
      body: JSON.stringify(requestPayload),
    });

    if (!response.ok) {
      throw new Error(`Conversion failed with status: ${response.status}`);
    }

    // Parse JSON response
    const responseData = await response.json();

    // Extract PDF base64 from response
    // Expected response format: { type: "content", data: { content: { converted_files: [{ filename, content_base64 }] } } }
    const convertedFiles = responseData.data?.content?.converted_files;
    
    if (!convertedFiles || convertedFiles.length === 0) {
      throw new Error('Conversion response missing PDF data');
    }

    const convertedFile = convertedFiles[0];
    const pdfBase64 = convertedFile.content_base64;

    if (!pdfBase64) {
      throw new Error('Conversion response missing base64 content');
    }

    // Return with original filename to avoid confusion
    return {
      name: file.name,
      type: 'application/pdf',
      data: pdfBase64
    };

  } catch (error) {
    console.error('[file-conversion] Conversion failed:', error);
    
    // FALLBACK: Return original file data
    // Convert original file to base64
    const originalBase64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          const base64 = result.split(',')[1];
          resolve(base64);
        } else if (result instanceof ArrayBuffer) {
          resolve(arrayBufferToBase64(result));
        } else {
          reject(new Error('Unexpected result type'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    console.warn('[file-conversion] Using fallback - returning original file');
    return {
      name: file.name,
      type: file.type,
      data: originalBase64
    };
  }
}